import { useRouter } from 'next/router';
import InputMask from 'react-input-mask';
import React, { useRef, useState, useEffect } from 'react';

import { isPostCode } from '../../helpers/form';
import { getString } from '../../helpers/styles';
import icons from '../../shared/assets/icons/redesign';
import DateDropdown from '../DateDropdown/DateDropdown.jsx';
import { trackAction, usePlatform } from '../../helpers/tracking';

import './CoursesPaymentForm.scss';

const { Check, Logo, IdealPayment, Info } = icons;

const duration = {
  'online-cursussen-basis': '12',
  'online-cursussen-plus': '18',
  'online-cursussen-pro': '36',
  'online-cursussen-ouder-en-kind-basis': '12',
  'online-cursussen-ouder-en-kind-plus': '18',
  'online-cursussen-ouder-en-kind-pro': '36'
};

export default function CoursesPaymentForm({
  stage,
  paymentLink,
  nextStageLink,
  paymentItemType,
  changeUserData,
  paymentItemPrice,
  paymentLinkError,
  startPolling,
  stopPolling,
  courseName
}) {
  const router = useRouter();
  const [checked, setChecked] = useState(false);

  const form = useRef();
  const button = useRef();
  const checkbox = useRef();
  const dateDropdown = useRef();
  const postCodeInput = useRef();
  const platform = usePlatform();
  const [paymentLinkLoaded, setPaymentLinkLoaded] = useState(false);

  const firstStage = stage === 1;

  useEffect(() => {
    checkFormValidity();
  }, [checked]);

  useEffect(() => {
    if (!firstStage)
      if (paymentLinkError) {
        startPolling(1000);
      } else {
        stopPolling();
        setPaymentLinkLoaded(true);
      }
  }, [firstStage, paymentLinkError, startPolling, stopPolling]);

  return (
    <div className="coursePaymentForm">
      <div className="coursePaymentForm-header">
        <Logo />

        <span
          className="coursePaymentForm-close-icon"
          onClick={() => {
            router.back();

            trackAction(
              platform,
              router.pathname ===
                ('/zwangerschapscursus/betalen' ||
                  '/zwangerschapscursus-2/betalen' ||
                  '/zwangerschapscursus-3/betalen')
                ? 'coursesbetalen'
                : 'coursesbetalenideal',
              'clickclosebutton'
            );
          }}
        >
          &times;
        </span>
      </div>

      <form ref={form} name="payment" className="coursePaymentForm-content">
        {firstStage ? (
          <>
            {!courseName && (
              <div className="coursePaymentForm-alert">
                <Info className="coursePaymentForm-alert-icon" />

                <p className="coursePaymentForm-alert-text">
                  Met onderstaande gegevens wordt de factuur opgemaakt die je
                  kunt indienen bij je zorgverzekeraar. Deze kunnen niet meer
                  gewijzigd worden.
                </p>
              </div>
            )}

            <label className="coursePaymentForm-label">
              Straat en huisnummer
              <input
                type="text"
                required
                name="payAddress"
                onChange={() => checkFormValidity()}
                className="coursePaymentForm-input"
                placeholder="Het woonadres dat bekend is bij je zorgverzekering"
              />
            </label>

            <label className="coursePaymentForm-label">
              Postcode
              <InputMask
                required
                mask="9999-aa"
                name="payPostalcode"
                ref={postCodeInput}
                className="
                  coursePaymentForm-input
                  coursePaymentForm-postal-code-input
                "
                onChange={() => checkFormValidity()}
                placeholder="De postcode die bekend is bij je zorgverzekering"
              />
            </label>

            <label className="coursePaymentForm-label">
              Woonplaats
              <input
                type="text"
                required
                name="payCity"
                onChange={() => checkFormValidity()}
                className="coursePaymentForm-input"
                placeholder="De woonplaats die bekend is bij je zorgverzekering"
              />
            </label>

            <label className="coursePaymentForm-label">
              Jouw geboortedatum
              <DateDropdown
                reference={dateDropdown}
                onChange={checkFormValidity}
              />
            </label>

            <div
              className={getString([
                {
                  value: 'courses-checkbox',
                  condition: true
                },
                {
                  value: 'courses-checkbox-checked',
                  condition: checked
                }
              ])}
              onClick={() => setChecked(!checked)}
            >
              <span className="courses-checkmark">
                <Check className="courses-checkmark-content" />
              </span>

              <p className="coursePaymentForm-text">
                Ik ga akkoord met de toepasselijkheid van de{' '}
                <a
                  target="_blank"
                  className="coursePaymentForm-link"
                  href="/privacyverklaring"
                >
                  Algemene Voorwaarden
                </a>{' '}
                op deze bestelling
              </p>
            </div>
          </>
        ) : (
          <>
            <div>
              <h3 className="coursePaymentForm-title">
                {courseName ? (
                  <>Toegang tot {courseName}.</>
                ) : (
                  `${duration[paymentItemType]} maanden onbeperkt toegang tot alle online cursussen`
                )}
              </h3>

              <div
                onClick={() => {
                  checkbox.current.checked = !checkbox.current.checked;

                  checkFormValidity();

                  trackAction(
                    platform,
                    'coursesbetalenideal',
                    'clickidealcheckbox'
                  );
                }}
                className="coursePaymentForm-payment-method"
              >
                <div>
                  <IdealPayment className="coursePaymentForm-payment-method-icon" />

                  <p className="coursePaymentForm-payment-method-label">
                    Ideal
                  </p>
                </div>

                <div className="coursePaymentForm-item-checkbox-wrap">
                  <input
                    ref={checkbox}
                    type="checkbox"
                    required
                    className="coursePaymentForm-item-checkbox"
                  />

                  <span className="coursePaymentForm-item-checkmark-wrap">
                    <span className="coursePaymentForm-item-checkmark">
                      <Check className="coursePaymentForm-item-checkmark-icon" />
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </form>

      <div className="coursePaymentForm-cart">
        <p className="coursePaymentForm-cart-title">Winkelmandje</p>

        <p className="coursePaymentForm-cart-subtitle">
          {courseName ||
            `${duration[paymentItemType]} maanden onbeperkt toegang tot alle online cursussen`}
        </p>
        <span className="coursePaymentForm-cart-price">
          € {paymentItemPrice}
        </span>
      </div>

      <button
        ref={button}
        className="coursePaymentForm-button coursePaymentForm-button-disabled"
        onClick={() => {
          if (
            !form.current.checkValidity() ||
            (postCodeInput.current && !isPostCode(postCodeInput.current.value))
          ) {
            return form.current.reportValidity();
          } else if (firstStage) {
            router.push(nextStageLink);

            const { payAddress, payPostalcode, payCity } = form.current;

            changeUserData({
              variables: {
                payCity: payCity.value,
                payAddress: payAddress.value,
                payBirthdate: dateDropdown.current.dataset.value,
                payPostalcode: payPostalcode.value.replace('-', '')
              }
            });

            trackAction(platform, 'coursesbetalen', 'clicknextbutton');
          } else {
            if (!paymentLinkLoaded) return;

            location.href = paymentLink;

            trackAction(platform, 'coursesbetalenideal', 'clicknextbutton');
          }
        }}
      >
        Verder
      </button>
    </div>
  );

  function checkFormValidity() {
    if (
      !form.current.checkValidity() ||
      (!firstStage && !paymentLinkLoaded) ||
      (checkbox.current && !checkbox.current.checked) ||
      (postCodeInput.current &&
        (!checked || !isPostCode(postCodeInput.current.value)))
    )
      button.current.classList.add('coursePaymentForm-button-disabled');
    else button.current.classList.remove('coursePaymentForm-button-disabled');
  }
}
